import { useConfirmingLeave } from "@lhd/app/index";
import { useCallback, useContext, useEffect, useRef } from "react";
import { UNSAFE_DataRouterContext } from "react-router-dom";

type RedirectAction = () => void;

/**
 * Guards redirects made anywhere in the current screen while this hook is
 * mounted. The confirmation UI is provided by the global CommonLayout modal.
 */
export const useConfirmRedirect = (needConfirm: boolean) => {
  const dataRouterContext = useContext(UNSAFE_DataRouterContext);
  const router = dataRouterContext?.router;
  const { confirmAction, setNeedConfirm } = useConfirmingLeave();
  const needConfirmRef = useRef(needConfirm);
  const allowNextPopStateRef = useRef(false);
  const isRestoringHistoryRef = useRef(false);
  const pendingHistoryDeltaRef = useRef<number>();
  const currentHistoryIndexRef = useRef<number | undefined>(
    window.history.state?.idx,
  );

  useEffect(() => {
    needConfirmRef.current = needConfirm;
    setNeedConfirm(needConfirm);
  }, [needConfirm, setNeedConfirm]);

  useEffect(
    () => () => {
      setNeedConfirm(false);
    },
    [setNeedConfirm],
  );

  const confirmRedirect = useCallback(
    (redirect: RedirectAction) => {
      confirmAction(redirect);
    },
    [confirmAction],
  );

  useEffect(() => {
    if (!router) return;

    // React Router 6.4 has no public blocker hook. Guard its shared navigate
    // method so Link/useNavigate calls from the screen and layout are covered.
    const originalNavigate = router.navigate;
    const navigateWithConfirm = ((to: unknown, options?: unknown) => {
      confirmRedirect(() => {
        if (typeof to === "number" && needConfirmRef.current) {
          allowNextPopStateRef.current = true;
        }
        (originalNavigate as any)(to, options);
      });
    }) as typeof router.navigate;

    router.navigate = navigateWithConfirm;

    return () => {
      if (router.navigate === navigateWithConfirm) {
        router.navigate = originalNavigate;
      }
    };
  }, [confirmRedirect, router]);

  useEffect(() => {
    const originalOpen = window.open;
    const openWithConfirm = ((
      url?: string | URL,
      target?: string,
      features?: string,
    ) => {
      if (!needConfirmRef.current) {
        return originalOpen.call(window, url, target, features);
      }

      confirmRedirect(() => {
        originalOpen.call(window, url, target, features);
      });
      return null;
    }) as typeof window.open;

    window.open = openWithConfirm;

    return () => {
      if (window.open === openWithConfirm) {
        window.open = originalOpen;
      }
    };
  }, [confirmRedirect]);

  useEffect(() => {
    if (!router) return;

    return router.subscribe(() => {
      currentHistoryIndexRef.current = window.history.state?.idx;
    });
  }, [router]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const nextHistoryIndex = event.state?.idx;

      if (allowNextPopStateRef.current) {
        allowNextPopStateRef.current = false;
        currentHistoryIndexRef.current = nextHistoryIndex;
        return;
      }

      if (isRestoringHistoryRef.current) {
        event.preventDefault();
        event.stopImmediatePropagation();
        isRestoringHistoryRef.current = false;

        const restoreDelta = pendingHistoryDeltaRef.current;
        pendingHistoryDeltaRef.current = undefined;
        if (!restoreDelta) return;

        confirmRedirect(() => {
          allowNextPopStateRef.current = true;
          window.history.go(-restoreDelta);
        });
        return;
      }

      if (!needConfirmRef.current) {
        currentHistoryIndexRef.current = nextHistoryIndex;
        return;
      }

      const currentHistoryIndex = currentHistoryIndexRef.current;
      if (
        typeof currentHistoryIndex !== "number" ||
        typeof nextHistoryIndex !== "number"
      ) {
        return;
      }

      const restoreDelta = currentHistoryIndex - nextHistoryIndex;
      if (restoreDelta === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      isRestoringHistoryRef.current = true;
      pendingHistoryDeltaRef.current = restoreDelta;
      window.history.go(restoreDelta);
    };

    window.addEventListener("popstate", handlePopState, true);
    return () => window.removeEventListener("popstate", handlePopState, true);
  }, [confirmRedirect]);

  useEffect(() => {
    if (!needConfirm) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [needConfirm]);
};

export default useConfirmRedirect;
